import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { useAuth, apiCall } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

// Constants
const CITIES = ["Delhi", "Mumbai", "Bangalore", "Chennai", "Kolkata", "Hyderabad", "Pune", "Ahmedabad"];
const RELIGIONS = ["Hindu", "Muslim", "Christian", "Sikh", "Buddhist", "Jain"];

const Onboarding = () => {
  const { user, token, updateUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showOtherCity, setShowOtherCity] = useState(false);
  const [showOtherSelfReligion, setShowOtherSelfReligion] = useState(false);
  const [showOtherSpouseReligion, setShowOtherSpouseReligion] = useState(false);
  
  const [profile, setProfile] = useState({
    full_name: user?.name || "",
    city: "",
    city_other: "",
    country: user?.country || "",
    age_group: "",
    gender: "",
    relationship_status: "",
    marriage_type: "",
    self_religion: "",
    self_religion_other: "",
    spouse_religion: "",
    spouse_religion_other: "",
    relationship_duration: "",
    living_situation: "",
    num_kids: "0",
    income_status: "",
    spiritual_path: "",
  });

  const updateProfile = (key, value) => {
    setProfile(prev => ({ ...prev, [key]: value }));
    
    // Handle "Other" option visibility
    if (key === "city") {
      setShowOtherCity(value === "Other");
      if (value !== "Other") setProfile(prev => ({ ...prev, city_other: "" }));
    }
    if (key === "self_religion") {
      setShowOtherSelfReligion(value === "Other");
      if (value !== "Other") setProfile(prev => ({ ...prev, self_religion_other: "" }));
    }
    if (key === "spouse_religion") {
      setShowOtherSpouseReligion(value === "Other");
      if (value !== "Other") setProfile(prev => ({ ...prev, spouse_religion_other: "" }));
    }
  };

  const handleNext = () => {
    if (step < 4) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    // Prepare data - resolve "Other" values
    const saveData = {
      ...profile,
      city: profile.city === "Other" ? profile.city_other : profile.city,
      self_religion: profile.self_religion === "Other" ? profile.self_religion_other : profile.self_religion,
      spouse_religion: profile.spouse_religion === "Other" ? profile.spouse_religion_other : profile.spouse_religion,
      num_kids: parseInt(profile.num_kids) || 0,
    };
    
    // Remove temporary fields
    delete saveData.city_other;
    delete saveData.self_religion_other;
    delete saveData.spouse_religion_other;
    
    const result = await apiCall("PUT", "/profile", saveData, token);
    setLoading(false);
    
    if (result.success) {
      updateUser({ profile_complete: true });
      toast.success("Profile completed! Welcome to your journey.");
      navigate("/dashboard");
    } else {
      toast.error(result.error);
    }
  };

  const showRelationshipFields = ["Committed", "Married", "Divorced", "Separated", "Widowed", "Complicated"].includes(profile.relationship_status);

  const renderStep1 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <span className="label-sacred">STEP 1 OF 4</span>
        <h2 className="font-heading text-2xl text-divine-gold mt-2">Core Identity</h2>
      </div>

      <div className="space-y-4">
        <div>
          <Label className="label-sacred">Full Name *</Label>
          <Input
            value={profile.full_name}
            onChange={(e) => updateProfile("full_name", e.target.value)}
            className="mt-2 bg-neutral-obsidian/50 border border-divine-gold/20 focus:border-divine-gold text-chakra-crown"
            data-testid="fullname-input"
          />
        </div>

        <div>
          <Label className="label-sacred">City *</Label>
          <div className="space-y-2 mt-2">
            <Select value={profile.city} onValueChange={(v) => updateProfile("city", v)}>
              <SelectTrigger className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown" data-testid="city-select">
                <SelectValue placeholder="Select your city" />
              </SelectTrigger>
              <SelectContent className="bg-cosmic-deep border border-divine-gold/20">
                {CITIES.map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
            {showOtherCity && (
              <Input
                value={profile.city_other}
                onChange={(e) => updateProfile("city_other", e.target.value)}
                placeholder="Enter your city name"
                className="bg-neutral-obsidian/50 border border-divine-gold/20 focus:border-divine-gold text-chakra-crown"
                data-testid="city-other-input"
              />
            )}
          </div>
        </div>

        <div>
          <Label className="label-sacred">Country *</Label>
          <Select value={profile.country} onValueChange={(v) => updateProfile("country", v)}>
            <SelectTrigger className="mt-2 bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown" data-testid="country-select">
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent className="bg-cosmic-deep border border-divine-gold/20 max-h-60">
              <SelectItem value="India">India</SelectItem>
              <SelectItem value="United States">United States</SelectItem>
              <SelectItem value="United Kingdom">United Kingdom</SelectItem>
              <SelectItem value="Canada">Canada</SelectItem>
              <SelectItem value="Australia">Australia</SelectItem>
              <SelectItem value="Singapore">Singapore</SelectItem>
              <SelectItem value="UAE">UAE</SelectItem>
              <SelectItem value="Germany">Germany</SelectItem>
              <SelectItem value="Netherlands">Netherlands</SelectItem>
              <SelectItem value="New Zealand">New Zealand</SelectItem>
              <SelectItem value="South Africa">South Africa</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="label-sacred">Age Group *</Label>
          <Select value={profile.age_group} onValueChange={(v) => updateProfile("age_group", v)}>
            <SelectTrigger className="mt-2 bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown" data-testid="age-select">
              <SelectValue placeholder="Select age group" />
            </SelectTrigger>
            <SelectContent className="bg-cosmic-deep border border-divine-gold/20">
              <SelectItem value="18-25">18-25</SelectItem>
              <SelectItem value="26-35">26-35</SelectItem>
              <SelectItem value="36-45">36-45</SelectItem>
              <SelectItem value="46-55">46-55</SelectItem>
              <SelectItem value="55+">55+</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="label-sacred">Gender *</Label>
          <RadioGroup
            value={profile.gender}
            onValueChange={(v) => updateProfile("gender", v)}
            className="flex gap-4 mt-2"
          >
            {["Male", "Female", "Other"].map((g) => (
              <div key={g} className="flex items-center space-x-2">
                <RadioGroupItem value={g} id={g} className="border-divine-gold/40 text-divine-gold" />
                <Label htmlFor={g} className="text-chakra-crown">{g}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>
      </div>
    </motion.div>
  );

  const renderStep2 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <span className="label-sacred">STEP 2 OF 4</span>
        <h2 className="font-heading text-2xl text-divine-gold mt-2">Relational Context</h2>
      </div>

      <div className="space-y-4">
        <div>
          <Label className="label-sacred">Relationship Status *</Label>
          <Select value={profile.relationship_status} onValueChange={(v) => updateProfile("relationship_status", v)}>
            <SelectTrigger className="mt-2 bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown" data-testid="status-select">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent className="bg-cosmic-deep border border-divine-gold/20">
              <SelectItem value="Single">Single</SelectItem>
              <SelectItem value="Dating">Dating</SelectItem>
              <SelectItem value="Committed">Committed</SelectItem>
              <SelectItem value="Married">Married</SelectItem>
              <SelectItem value="Separated">Separated</SelectItem>
              <SelectItem value="Divorced">Divorced</SelectItem>
              <SelectItem value="Widowed">Widowed</SelectItem>
              <SelectItem value="Complicated">It's Complicated</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {showRelationshipFields && (
          <>
            <div>
              <Label className="label-sacred">Marriage Type</Label>
              <Select value={profile.marriage_type} onValueChange={(v) => updateProfile("marriage_type", v)}>
                <SelectTrigger className="mt-2 bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-cosmic-deep border border-divine-gold/20">
                  <SelectItem value="Love Marriage">Love Marriage</SelectItem>
                  <SelectItem value="Arranged Marriage">Arranged Marriage</SelectItem>
                  <SelectItem value="Love-cum-Arranged">Love-cum-Arranged</SelectItem>
                  <SelectItem value="Not applicable">Not applicable</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="label-sacred">Your Religion</Label>
              <div className="space-y-2 mt-2">
                <Select value={profile.self_religion} onValueChange={(v) => updateProfile("self_religion", v)}>
                  <SelectTrigger className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown">
                    <SelectValue placeholder="Select religion" />
                  </SelectTrigger>
                  <SelectContent className="bg-cosmic-deep border border-divine-gold/20">
                    {RELIGIONS.map(r => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {showOtherSelfReligion && (
                  <Input
                    value={profile.self_religion_other}
                    onChange={(e) => updateProfile("self_religion_other", e.target.value)}
                    placeholder="Enter your religion"
                    className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown"
                  />
                )}
              </div>
            </div>

            <div>
              <Label className="label-sacred">Spouse's Religion</Label>
              <div className="space-y-2 mt-2">
                <Select value={profile.spouse_religion} onValueChange={(v) => updateProfile("spouse_religion", v)}>
                  <SelectTrigger className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown">
                    <SelectValue placeholder="Select religion" />
                  </SelectTrigger>
                  <SelectContent className="bg-cosmic-deep border border-divine-gold/20">
                    {RELIGIONS.map(r => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {showOtherSpouseReligion && (
                  <Input
                    value={profile.spouse_religion_other}
                    onChange={(e) => updateProfile("spouse_religion_other", e.target.value)}
                    placeholder="Enter spouse's religion"
                    className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown"
                  />
                )}
              </div>
            </div>
          </>
        )}

        <div>
          <Label className="label-sacred">Relationship Duration *</Label>
          <Select value={profile.relationship_duration} onValueChange={(v) => updateProfile("relationship_duration", v)}>
            <SelectTrigger className="mt-2 bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown">
              <SelectValue placeholder="Select duration" />
            </SelectTrigger>
            <SelectContent className="bg-cosmic-deep border border-divine-gold/20">
              <SelectItem value="<1 year">Less than 1 year</SelectItem>
              <SelectItem value="1-3 years">1-3 years</SelectItem>
              <SelectItem value="3-5 years">3-5 years</SelectItem>
              <SelectItem value="5-10 years">5-10 years</SelectItem>
              <SelectItem value="10-15 years">10-15 years</SelectItem>
              <SelectItem value="15+ years">15+ years</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </motion.div>
  );

  const renderStep3 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <span className="label-sacred">STEP 3 OF 4</span>
        <h2 className="font-heading text-2xl text-divine-gold mt-2">Life Situation</h2>
      </div>

      <div className="space-y-4">
        <div>
          <Label className="label-sacred">Living Situation</Label>
          <Select value={profile.living_situation} onValueChange={(v) => updateProfile("living_situation", v)}>
            <SelectTrigger className="mt-2 bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown">
              <SelectValue placeholder="Select situation" />
            </SelectTrigger>
            <SelectContent className="bg-cosmic-deep border border-divine-gold/20">
              <SelectItem value="Separate house (Independent)">Separate house (Independent)</SelectItem>
              <SelectItem value="Shared house (Family/In-laws)">Shared house (Family/In-laws)</SelectItem>
              <SelectItem value="Same house as partner">Same house as partner</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="label-sacred">Number of Children</Label>
          <Select value={profile.num_kids} onValueChange={(v) => updateProfile("num_kids", v)}>
            <SelectTrigger className="mt-2 bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent className="bg-cosmic-deep border border-divine-gold/20">
              <SelectItem value="0">0</SelectItem>
              <SelectItem value="1">1</SelectItem>
              <SelectItem value="2">2</SelectItem>
              <SelectItem value="3">3</SelectItem>
              <SelectItem value="4+">4+</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="label-sacred">Income Status</Label>
          <Select value={profile.income_status} onValueChange={(v) => updateProfile("income_status", v)}>
            <SelectTrigger className="mt-2 bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent className="bg-cosmic-deep border border-divine-gold/20">
              <SelectItem value="Independent earner">Independent earner</SelectItem>
              <SelectItem value="Non-earning (dependent on spouse)">Non-earning (dependent on spouse)</SelectItem>
              <SelectItem value="Non-earning (independent support)">Non-earning (independent support)</SelectItem>
              <SelectItem value="Dual income with partner">Dual income with partner</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </motion.div>
  );

  const renderStep4 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <span className="label-sacred">STEP 4 OF 4</span>
        <h2 className="font-heading text-2xl text-divine-gold mt-2">Spiritual Inclination</h2>
      </div>

      <div>
        <Label className="label-sacred mb-4 block">Your Natural Path *</Label>
        <RadioGroup
          value={profile.spiritual_path}
          onValueChange={(v) => updateProfile("spiritual_path", v)}
          className="grid gap-4"
        >
          {[
            { value: "KARMA YOGI", desc: "Duty: I serve/structure/protect" },
            { value: "BHAKTI YOGI", desc: "Devotion: I connect through love/music" },
            { value: "JNANA YOGI", desc: "Wisdom: I seek truth/self-analysis" },
            { value: "MATERIALIST", desc: "Practical: Simple tools only" }
          ].map((path) => (
            <div 
              key={path.value}
              className={`p-4 rounded-xl border cursor-pointer transition-all ${
                profile.spiritual_path === path.value 
                  ? "border-divine-gold bg-divine-gold/10" 
                  : "border-divine-gold/20 hover:border-divine-gold/40"
              }`}
              onClick={() => updateProfile("spiritual_path", path.value)}
            >
              <div className="flex items-center gap-3">
                <RadioGroupItem value={path.value} className="border-divine-gold/40 text-divine-gold" />
                <div>
                  <p className="text-divine-gold font-medium">{path.value}</p>
                  <p className="text-chakra-crown/60 text-sm">{path.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </RadioGroup>
      </div>

      <div className="p-4 rounded-xl bg-cosmic-light/20 border border-divine-gold/10 mt-6">
        <p className="text-sm text-chakra-crown/70 text-center">
          <span className="text-divine-gold">Your path guides your transformation.</span>
          <br />You can update your full profile anytime from your dashboard.
        </p>
      </div>
    </motion.div>
  );

  return (
    <div 
      className="min-h-screen bg-cosmic-void flex items-center justify-center px-6 py-12"
      data-testid="onboarding-page"
    >
      <div className="w-full max-w-lg">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            {[1, 2, 3, 4].map((s) => (
              <div 
                key={s}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  s < step ? "bg-divine-gold text-cosmic-void" :
                  s === step ? "bg-divine-gold/20 text-divine-gold border-2 border-divine-gold" :
                  "bg-cosmic-deep text-chakra-crown/40"
                }`}
              >
                {s < step ? <Check className="w-4 h-4" /> : s}
              </div>
            ))}
          </div>
          <div className="h-1 bg-cosmic-deep rounded-full">
            <div 
              className="h-full bg-divine-gold rounded-full transition-all duration-500"
              style={{ width: `${((step - 1) / 3) * 100}%` }}
            />
          </div>
        </div>

        {/* Client ID Display */}
        {user?.client_id && (
          <div className="text-center mb-6">
            <span className="label-sacred">Your Client ID</span>
            <p className="text-divine-gold font-mono text-lg mt-1" data-testid="client-id">
              {user.client_id}
            </p>
          </div>
        )}

        {/* Form Card */}
        <div className="sacred-card">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}

          {/* Navigation */}
          <div className="flex justify-between mt-8">
            <Button
              variant="ghost"
              onClick={handleBack}
              disabled={step === 1}
              className="text-chakra-crown hover:text-divine-gold"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>

            {step < 4 ? (
              <Button
                onClick={handleNext}
                className="vedic-button"
                data-testid="next-btn"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading || !profile.spiritual_path}
                className="vedic-button"
                data-testid="complete-btn"
              >
                {loading ? "Saving..." : "Complete Profile"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
